import React from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

export interface RoleRouteGuardProps {
  /**
   * Allowed global roles (e.g. ['administrador', 'corporacion', 'medico'])
   */
  allowedGlobalRoles?: string[];
  /**
   * Required center-level role ('admin' | 'user')
   */
  requiredCenterRole?: 'admin' | 'user';
  /**
   * Whether Superadmin (isAdmin === true) bypasses restrictions (default: true)
   */
  /**
   * If both allowedGlobalRoles and requiredCenterRole are supplied,
   * matchAny controls whether access is granted if EITHER matches (default: true)
   * or if BOTH must match (false).
   */
  matchAny?: boolean;
  allowAdminBypass?: boolean;
  /**
   * Redirection target when access is denied (default: '/home')
   */
  redirectTo?: string;
  children?: React.ReactNode;
}

/**
 * RoleRouteGuard (OWASP A01:2021 - Broken Access Control & RBAC Protection)
 * Prevents unauthorized users from mounting restricted routes via direct URL manipulation.
 */
export const RoleRouteGuard: React.FC<RoleRouteGuardProps> = ({
  allowedGlobalRoles,
  requiredCenterRole,
  matchAny = true,
  allowAdminBypass = true,
  redirectTo = '/home',
  children,
}) => {
  const { user } = useSelector((s: any) => s.auth || {});
  const { selectCenter } = useSelector((s: any) => s.selectedCenter || {});
  const location = useLocation();

  if (!user) {
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  // Superadmin / Owner bypass
  const isSuperAdmin = Boolean(user?.isAdmin === true || user?.isAdmin === 1);
  if (isSuperAdmin) {
    if (!allowAdminBypass) {
      return <Navigate to={redirectTo} replace />;
    }
    return children ? <>{children}</> : <Outlet />;
  }

  const hasGlobalRole =
    allowedGlobalRoles && allowedGlobalRoles.length > 0
      ? allowedGlobalRoles.includes(user?.role)
      : null;

  const hasCenterRole =
    requiredCenterRole
      ? selectCenter?.role === requiredCenterRole
      : null;

  let isAuthorized = false;

  if (hasGlobalRole !== null && hasCenterRole !== null) {
    isAuthorized = matchAny ? (hasGlobalRole || hasCenterRole) : (hasGlobalRole && hasCenterRole);
  } else if (hasGlobalRole !== null) {
    isAuthorized = hasGlobalRole;
  } else if (hasCenterRole !== null) {
    isAuthorized = hasCenterRole;
  } else {
    isAuthorized = true;
  }

  if (!isAuthorized) {
    return <Navigate to={redirectTo} replace />;
  }

  return children ? <>{children}</> : <Outlet />;
};

export default RoleRouteGuard;
